import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IconMessageCircle, IconAlertCircle, IconCheck, IconClock } from "@tabler/icons-react";
import { socket } from "../../socket";
import { useAuth } from "../../AuthContext";
import Dialog from "../core/dialogs";
import { fetchAwaitingUserCount } from "../../services/supportTickets";
import PageTitle from "../core/page-title";

const translations = {
    en: {
        pageTitle: "Support Tickets",
        pageSubtitle: "Create a request or track the status of existing tickets.",
        newTicketTitle: "Need help?",
        newTicketSubtitle: "Describe your issue and our support team will get back to you.",
        comingSoonNote: "API integration is coming soon. Meanwhile, use the template below.",
        sampleTicketsTitle: "Recent tickets",
        emptyState: "No tickets yet. Click “Create Ticket” to start the conversation.",
        createTicket: "Create Ticket",
        priority: "Priority",
        status: "Status",
        updated: "Updated",
        waitingSupport: "Waiting for support",
        awaitingUser: "Waiting for user",
        inProgress: "In progress",
        resolved: "Resolved",
        closed: "Closed",
        subject: "Subject",
        description: "Description",
        ticketCreated: "Ticket created successfully. We will get back to you soon.",
        validationMissing: "Subject and description are required.",
        genericError: "Something went wrong. Please try again.",
        authMissing: "You need to sign in again.",
        listTitle: "Your tickets",
        filterStatus: "Status filter",
        filterPriority: "Priority filter",
        filterAll: "All",
        messagesTitle: "Conversation",
        noTicketSelected: "Select a ticket to see full details and conversation.",
        replyPlaceholder: "Type your reply…",
        sendReply: "Send Reply",
        replySuccess: "Reply sent.",
        statusUpdateSuccess: "Ticket status updated.",
        markResolved: "Mark as resolved",
        reopenTicket: "Reopen ticket",
        priorityLow: "Low",
        priorityMedium: "Medium",
        priorityHigh: "High",
    },
    ru: {
        pageTitle: "Тикеты поддержки",
        pageSubtitle: "Создавайте обращения и отслеживайте их статус.",
        newTicketTitle: "Нужна помощь?",
        newTicketSubtitle: "Опишите проблему, и команда поддержки свяжется с вами.",
        comingSoonNote: "Интеграция с API появится позже. Пока используйте шаблон ниже.",
        sampleTicketsTitle: "Последние тикеты",
        emptyState: "Пока нет тикетов. Нажмите «Создать тикет», чтобы начать диалог.",
        createTicket: "Создать тикет",
        priority: "Приоритет",
        status: "Статус",
        updated: "Обновлено",
        waitingSupport: "Ожидает поддержку",
        awaitingUser: "Ожидает ответа",
        inProgress: "В работе",
        resolved: "Решено",
        closed: "Закрыт",
        subject: "Тема",
        description: "Описание",
        ticketCreated: "Тикет создан. Поддержка скоро свяжется с вами.",
        validationMissing: "Заполните тему и описание.",
        genericError: "Произошла ошибка. Попробуйте еще раз.",
        authMissing: "Необходимо войти в систему.",
        listTitle: "Ваши тикеты",
        filterStatus: "Фильтр по статусу",
        filterPriority: "Фильтр по приоритету",
        filterAll: "Все",
        messagesTitle: "Переписка",
        noTicketSelected: "Выберите тикет, чтобы увидеть подробности и переписку.",
        replyPlaceholder: "Введите ответ…",
        sendReply: "Отправить ответ",
        replySuccess: "Ответ отправлен.",
        statusUpdateSuccess: "Статус тикета обновлен.",
        markResolved: "Закрыть тикет",
        reopenTicket: "Переоткрыть тикет",
        priorityLow: "Низкий",
        priorityMedium: "Средний",
        priorityHigh: "Высокий",
    },
    ua: {
        pageTitle: "Тікети підтримки",
        pageSubtitle: "Створюйте звернення та відстежуйте їхній статус.",
        newTicketTitle: "Потрібна допомога?",
        newTicketSubtitle: "Опишіть проблему, і команда підтримки відповість найближчим часом.",
        comingSoonNote: "Підключення до API з’явиться пізніше. Поки що скористайтеся шаблоном нижче.",
        sampleTicketsTitle: "Останні тікети",
        emptyState: "Поки немає тікетів. Натисніть «Створити тікет», щоб розпочати діалог.",
        createTicket: "Створити тікет",
        priority: "Пріоритет",
        status: "Статус",
        updated: "Оновлено",
        waitingSupport: "Очікує підтримку",
        awaitingUser: "Очікує користувача",
        inProgress: "У роботі",
        resolved: "Вирішено",
        closed: "Закрито",
        subject: "Тема",
        description: "Опис",
        ticketCreated: "Тікет створено. Підтримка зв’яжеться з вами найближчим часом.",
        validationMissing: "Заповніть тему та опис.",
        genericError: "Сталася помилка. Спробуйте знову.",
        authMissing: "Потрібно виконати вхід.",
        listTitle: "Ваші тікети",
        filterStatus: "Фільтр за статусом",
        filterPriority: "Фільтр за пріоритетом",
        filterAll: "Усі",
        messagesTitle: "Листування",
        noTicketSelected: "Оберіть тікет, щоб побачити деталі та листування.",
        replyPlaceholder: "Введіть відповідь…",
        sendReply: "Надіслати відповідь",
        replySuccess: "Відповідь надіслано.",
        statusUpdateSuccess: "Статус тікета оновлено.",
        markResolved: "Закрити тікет",
        reopenTicket: "Відкрити знову",
        priorityLow: "Низький",
        priorityMedium: "Середній",
        priorityHigh: "Високий",
    },
};

const getInitialLang = () => (localStorage.getItem("lang") || "en").toLowerCase();
const useLanguage = (initialLang) => {
    const [lang, setLang] = useState(initialLang);
    useEffect(() => {
        const handler = (event) => {
            const next = (event?.detail?.lang || localStorage.getItem("lang") || "en").toLowerCase();
            setLang(next);
        };
        window.addEventListener("app:languageChanged", handler);
        window.addEventListener("storage", handler);
        return () => {
            window.removeEventListener("app:languageChanged", handler);
            window.removeEventListener("storage", handler);
        };
    }, []);
    return [lang, setLang];
};

const socketRequest = (event, responseEvent, payload = {}, timeoutMs = 15000) => {
    const requestId = `${event}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    return new Promise((resolve, reject) => {
        let settled = false;
        const timer = setTimeout(() => {
            if (settled) return;
            settled = true;
            socket.off(responseEvent, handler);
            reject(new Error("Request timed out"));
        }, timeoutMs);

        const handler = (response = {}) => {
            if (response.request_id !== requestId) return;
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            socket.off(responseEvent, handler);
            resolve(response);
        };

        socket.on(responseEvent, handler);
        socket.emit(event, { ...payload, request_id: requestId });
    });
};

const priorityBadge = (priority) => {
    switch (priority) {
        case "high":
            return "badge bg-danger-subtle text-danger";
        case "medium":
            return "badge bg-warning-subtle text-warning";
        default:
            return "badge bg-success-subtle text-success";
    }
};

const statusIcon = (status) => {
    switch (status) {
        case "waiting_support":
            return <IconClock size={18} className="text-warning me-2" />;
        case "awaiting_user":
            return <IconAlertCircle size={18} className="text-info me-2" />;
        case "resolved":
        default:
            return <IconCheck size={18} className="text-success me-2" />;
    }
};

const formatTimestamp = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }
    return date.toLocaleString();
};

const resolveWorkspaceId = (user = {}) => {
    const storedCompanyId = localStorage.getItem("companyId");
    const id =
        user?.current_workspace_id
        || user?.workspace_id
        || user?.selected_workspace_id
        || user?.workspace?.id
        || user?.company_id
        || storedCompanyId;

    if (!id) return null;
    const parsed = parseInt(id, 10);
    return Number.isNaN(parsed) ? id : parsed;
};

const formatAuthorName = (message = {}) => {
    const author = message.author || {};
    const first = author.first_name || author.firstName || "";
    const last = author.last_name || author.lastName || "";
    const fullFromAuthor = author.full_name || author.fullName || `${first} ${last}`.trim();
    const fallback =
        message.author_name ||
        message.authorName ||
        message.sender_name ||
        message.senderName ||
        message.user_name ||
        "";
    const email = author.email || message.author_email || "";
    const chosen = fullFromAuthor || fallback || email;
    return chosen || null;
};

const Tickets = () => {
    const { user } = useAuth();
    const [lang] = useLanguage(getInitialLang());
    const t = useMemo(() => translations[lang] || translations.en, [lang]);
    const formRef = useRef(null);
    const [subject, setSubject] = useState("");
    const [priority, setPriority] = useState("medium");
    const [description, setDescription] = useState("");
    const [formError, setFormError] = useState("");
    const [formSuccess, setFormSuccess] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [tickets, setTickets] = useState([]);
    const [listLoading, setListLoading] = useState(false);
    const [listError, setListError] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [priorityFilter, setPriorityFilter] = useState("all");
    const [selectedTicketId, setSelectedTicketId] = useState(null);
    const [ticketDetails, setTicketDetails] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailError, setDetailError] = useState("");
    const [replyText, setReplyText] = useState("");
    const [replySending, setReplySending] = useState(false);
    const [replyError, setReplyError] = useState("");
    const [replySuccess, setReplySuccess] = useState("");
    const [statusUpdating, setStatusUpdating] = useState(false);
    const [statusSuccess, setStatusSuccess] = useState("");
    const [globalAlert, setGlobalAlert] = useState({ type: "", text: "" });
    const statusOptions = useMemo(() => [
        { value: "all", label: t.filterAll },
        { value: "waiting_support", label: t.waitingSupport },
        { value: "awaiting_user", label: t.awaitingUser },
        { value: "in_progress", label: t.inProgress },
        { value: "resolved", label: t.resolved },
        { value: "closed", label: t.closed },
    ], [t]);
    const statusLabel = useCallback((value) => {
        switch ((value || "").toLowerCase()) {
            case "waiting_support":
                return t.waitingSupport;
            case "awaiting_user":
                return t.awaitingUser;
            case "resolved":
                return t.resolved;
            default:
                return value || "";
        }
    }, [t]);

    const workspaceId = resolveWorkspaceId(user);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const dismissGlobalAlert = () => setGlobalAlert({ type: "", text: "" });

    const normalizeTicketRow = (ticket = {}) => ({
        id: ticket.id || ticket.ticket_id || ticket.code || `#${Date.now()}`,
        title: ticket.subject || ticket.title || "",
        subject: ticket.subject || ticket.title || "",
        priority: ticket.priority || "medium",
        status: ticket.status || "waiting_support",
        updated_at: ticket.updated_at || ticket.last_reply_at || ticket.created_at || new Date().toISOString(),
        workspace_id: ticket.workspace_id || workspaceId || null,
    });

    const messagesContainerRef = useRef(null);

    const scrollMessagesToBottom = () => {
        const el = messagesContainerRef.current;
        if (el) {
            el.scrollTop = el.scrollHeight;
        }
    };

    const prependTicket = (ticket, initialMessage = null) => {
        const normalized = normalizeTicketRow(ticket);
        setTickets((current) => [normalized, ...current]);
        setSelectedTicketId(normalized.id);

        const normalizedMessages = Array.isArray(ticket.messages)
            ? ticket.messages.map(normalizeMessage)
            : [];
        if (initialMessage) {
            normalizedMessages.push(
                normalizeMessage({
                    ...initialMessage,
                    created_at: initialMessage.created_at || new Date().toISOString(),
                })
            );
        }

        setTicketDetails({
            ...ticket,
            id: normalized.id,
            subject: ticket.subject || normalized.subject,
            priority: normalized.priority,
            status: normalized.status,
            updated_at: normalized.updated_at,
            messages: normalizedMessages,
        });

        fetchTicketDetails(normalized.id);
    };

    const refreshAwaitingCounter = useCallback(async () => {
        if (!user?.token) return;
        const count = await fetchAwaitingUserCount(user.token, workspaceId, lang);
        window.dispatchEvent(new CustomEvent("supportTicketsCounterUpdate", { detail: { count } }));
    }, [user?.token, workspaceId, lang]);

    const fetchTickets = async () => {
        if (!user?.token) return;
        setListLoading(true);
        setListError("");
        try {
            const response = await socketRequest(
                "support_tickets:list",
                "support_tickets:list-response",
                {
                    token: user.token,
                    lang,
                    status: statusFilter,
                    priority: priorityFilter,
                    workspace_id: workspaceId,
                }
            );
            if (response.status === 1) {
                const rows = Array.isArray(response.data) ? response.data.map(normalizeTicketRow) : [];
                setTickets(rows);
                if (rows.length && !selectedTicketId) {
                    setSelectedTicketId(rows[0].id);
                    fetchTicketDetails(rows[0].id);
                }
                refreshAwaitingCounter();
            } else {
                setListError(response?.err?.message || t.genericError);
            }
        } catch (err) {
            setListError(err?.message || t.genericError);
        } finally {
            setListLoading(false);
        }
    };

    const fetchTicketDetails = async (ticketId) => {
        if (!user?.token || !ticketId) return;
        setDetailLoading(true);
        setDetailError("");
        setTicketDetails(null);
        try {
            const response = await socketRequest(
                "support_tickets:get",
                "support_tickets:get-response",
                {
                    token: user.token,
                    lang,
                    ticket_id: ticketId,
                    workspace_id: workspaceId,
                }
            );
            if (response.status === 1) {
                const ticket = response.data || {};
                const normalizedMessages = Array.isArray(ticket.messages)
                    ? ticket.messages.map(normalizeMessage)
                    : [];
                setTicketDetails({
                    ...ticket,
                    messages: normalizedMessages,
                });
                requestAnimationFrame(scrollMessagesToBottom);
            } else {
                setDetailError(response?.err?.message || t.genericError);
            }
        } catch (err) {
            setDetailError(err?.message || t.genericError);
        } finally {
            setDetailLoading(false);
        }
    };

    const handleCreateTicket = async (event) => {
        event.preventDefault();
        setFormError("");
        setFormSuccess("");

        if (!subject.trim() || !description.trim()) {
            setFormError(t.validationMissing);
            return;
        }
        if (!user?.token) {
            setFormError(t.authMissing);
            return;
        }

        setSubmitting(true);
        try {
            const response = await socketRequest(
                "support_tickets:create",
                "support_tickets:create-response",
                {
                    token: user.token,
                    lang,
                    workspace_id: workspaceId,
                    subject: subject.trim(),
                    description: description.trim(),
                    priority,
                }
            );

            if (response.status === 1) {
                const createdTicket = response.data?.ticket || response.data || {};
                const successMessage = response.message || t.ticketCreated;
                setFormSuccess(successMessage);
                setGlobalAlert({ type: "success", text: successMessage });
                const fallbackTicket = createdTicket.ticket || createdTicket.data || createdTicket || {};
                const fallbackId = fallbackTicket.id || fallbackTicket.ticket_id || Date.now();
                const authorName =
                    `${(user?.first_name || "").trim()} ${(user?.last_name || "").trim()}`.trim() || user?.email;

                // update list immediately
                prependTicket(
                    { ...fallbackTicket, id: fallbackId },
                    {
                        body: description.trim(),
                        sender: "user",
                        authorName,
                        created_at: new Date().toISOString(),
                    }
                );
                setSubject("");
                setDescription("");
                setPriority("medium");
                setShowCreateModal(false);
                refreshAwaitingCounter();
            } else {
                const errMsg = response?.err?.message || t.genericError;
                setFormError(errMsg);
                setGlobalAlert({ type: "error", text: errMsg });
            }
        } catch (err) {
            const errMsg = err?.message || t.genericError;
            setFormError(errMsg);
            setGlobalAlert({ type: "error", text: errMsg });
        } finally {
            setSubmitting(false);
        }
    };

    const openCreateModal = () => {
        setFormError("");
        setFormSuccess("");
        setShowCreateModal(true);
    };

    const closeCreateModal = () => {
        if (!submitting) {
            setShowCreateModal(false);
        }
    };

    const selectTicket = (ticket) => {
        setSelectedTicketId(ticket.id);
        fetchTicketDetails(ticket.id);
    };

    const normalizeMessage = (message = {}) => ({
        id: message.id || message.message_id || Date.now(),
        sender: message.sender || message.from || (message.author && message.author.type) || "user",
        authorName: message.sender_name || formatAuthorName(message),
        body: message.body || message.message || "",
        attachments: Array.isArray(message.attachments) ? message.attachments : [],
        created_at: message.created_at || message.sent_at || message.updated_at || new Date().toISOString(),
    });

    const appendMessage = (message) => {
        setTicketDetails((current) => {
            if (!current) return current;
            const normalized = normalizeMessage(message);
            const messages = Array.isArray(current.messages)
                ? [...current.messages, normalized]
                : [normalized];
            return {
                ...current,
                messages,
                updated_at: normalized.created_at || current.updated_at,
                status: normalized.sender === "support" ? current.status : "awaiting_support",
            };
        });
    };

    const handleReply = async (event) => {
        event.preventDefault();
        setReplyError("");
        setReplySuccess("");
        if (!replyText.trim()) {
            setReplyError(t.validationMissing);
            return;
        }
        if (!user?.token || !selectedTicketId) {
            setReplyError(t.genericError);
            return;
        }
        setReplySending(true);
        try {
            const response = await socketRequest(
                "support_tickets:reply",
                "support_tickets:reply-response",
                {
                    token: user.token,
                    lang,
                    ticket_id: selectedTicketId,
                    workspace_id: workspaceId,
                    message: replyText.trim(),
                }
            );
            if (response.status === 1) {
                const payload = response.data?.message || response.data?.data || response.data || {};
                if (!payload.body && replyText.trim()) {
                    payload.body = replyText.trim();
                }
                payload.sender = payload.sender || "user";
                payload.authorName = payload.authorName
                    || formatAuthorName(payload)
                    || `${(user?.first_name || "").trim()} ${(user?.last_name || "").trim()}`.trim()
                    || user?.email
                    || user?.user_name;
                appendMessage(payload);
                requestAnimationFrame(scrollMessagesToBottom);
                fetchTicketDetails(selectedTicketId);
                refreshAwaitingCounter();
                setReplyText("");
                setReplySuccess(response.message || t.replySuccess);
            } else {
                setReplyError(response?.err?.message || t.genericError);
            }
        } catch (err) {
            setReplyError(err?.message || t.genericError);
        } finally {
            setReplySending(false);
        }
    };

    const handleStatusChange = async (nextStatus) => {
        if (!selectedTicketId || !user?.token) return;
        setStatusUpdating(true);
        setStatusSuccess("");
        setDetailError("");
        try {
            const response = await socketRequest(
                "support_tickets:status",
                "support_tickets:status-response",
                {
                    token: user.token,
                    lang,
                    ticket_id: selectedTicketId,
                    workspace_id: workspaceId,
                    status: nextStatus,
                }
            );
            if (response.status === 1) {
                setTicketDetails((current) => current ? { ...current, ...response.data } : current);
                setStatusSuccess(response.message || t.statusUpdateSuccess);
                fetchTickets();
                refreshAwaitingCounter();
            } else {
                setDetailError(response?.err?.message || t.genericError);
            }
        } catch (err) {
            setDetailError(err?.message || t.genericError);
        } finally {
            setStatusUpdating(false);
        }
    };

    useEffect(() => {
        if (user?.token) {
            fetchTickets();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.token, statusFilter, priorityFilter]);

    return (
        <div className="container-fluid py-4 position-relative">
            <PageTitle
                title={t.pageTitle}
                subTitle={t.pageSubtitle}
                homeUrl="/dashboard"
                links={[{ name: t.pageTitle, link: "/tickets" }]}
                hero
            />
            {globalAlert.text && (
                <div className={`alert alert-${globalAlert.type === "error" ? "danger" : "success"} alert-dismissible fade show`} role="alert">
                    {globalAlert.text}
                    <button type="button" className="btn-close" aria-label="Close" onClick={dismissGlobalAlert} />
                </div>
            )}

            <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between mb-3">
                <div></div>
                <button
                    className="btn btn-primary d-flex align-items-center gap-2 mt-3 mt-md-0"
                    type="button"
                    onClick={openCreateModal}
                >
                    <IconMessageCircle size={18} />
                    {t.createTicket}
                </button>
            </div>

            <div className="row g-4">
                <div className="col-12 col-xl-5">

                    <div className="card">
                        <div className="card-body">
                            <div className="row g-2 mb-3">
                                <div className="col-sm-6">
                                    <label className="form-label text-muted small">{t.filterStatus}</label>
                                    <select
                                        className="form-select"
                                        value={statusFilter}
                                        onChange={(event) => setStatusFilter(event.target.value)}
                                        disabled={listLoading}
                                    >
                                        {statusOptions.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-sm-6">
                                    <label className="form-label text-muted small">{t.filterPriority}</label>
                                    <select
                                        className="form-select"
                                        value={priorityFilter}
                                        onChange={(event) => setPriorityFilter(event.target.value)}
                                        disabled={listLoading}
                                    >
                                        <option value="all">{t.filterAll}</option>
                                        <option value="high">{t.priorityHigh}</option>
                                        <option value="medium">{t.priorityMedium}</option>
                                        <option value="low">{t.priorityLow}</option>
                                    </select>
                                </div>
                            </div>
                            {listError && <div className="alert alert-danger">{listError}</div>}
                            {listLoading ? (
                                <div className="text-center py-4">
                                    <div className="spinner-border text-primary" role="status" />
                                </div>
                            ) : (
                                <>
                                    <div
                                        className="list-group"
                                        style={{
                                            maxHeight: "480px",
                                            overflowY: "auto",
                                            overflowX: "hidden",
                                        }}
                                    >
                                        {tickets.map((ticket) => {
                                            const active = selectedTicketId === ticket.id;
                                            return (
                                                <button
                                                    type="button"
                                                    key={ticket.id}
                                                    className={`list-group-item list-group-item-action rounded-3 mb-2 text-start border ${active ? "border-primary shadow-sm bg-primary-subtle" : "border-light-subtle shadow-sm bg-body"}`}
                                                    onClick={() => selectTicket(ticket)}
                                                >
                                                    <div className="d-flex justify-content-between align-items-start">
                                                        <div>
                                                            <div className="fw-semibold">{ticket.title || t.subject}</div>
                                                            <div className="text-muted small">
                                                                {ticket.id} · {t.updated}: {formatTimestamp(ticket.updated_at)}
                                                            </div>
                                                        </div>
                                                        <span className={priorityBadge(ticket.priority)}>
                                                            {t.priority}: {ticket.priority}
                                                        </span>
                                                    </div>
                                                    <div className="d-flex align-items-center mt-2 text-muted small">
                                                        {statusIcon(ticket.status)}
                                                        <span className="ms-1">
                                                            {t.status}: {statusLabel(ticket.status)}
                                                        </span>
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                    {!tickets.length && (
                                        <div className="text-center text-muted py-4">{t.emptyState}</div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>

                <div className="col-12 col-xl-7">
                    <div className="card h-100">
                        <div className="card-body d-flex flex-column">
                            {detailLoading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status" />
                                </div>
                            ) : ticketDetails ? (
                                <>
                                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3">
                                        <div>
                                            <h4 className="fw-semibold mb-1">{ticketDetails.subject}</h4>
                                            <div className="text-muted small">
                                                #{ticketDetails.id} · {t.updated}: {formatTimestamp(ticketDetails.updated_at)}
                                            </div>
                                        </div>
                                        <div className="d-flex flex-wrap gap-2 mt-3 mt-md-0">
                                            <span className={priorityBadge(ticketDetails.priority)}>
                                                {t.priority}: {ticketDetails.priority}
                                            </span>
                                            <span className="badge bg-primary-subtle text-primary">
                                                {t.status}: {statusLabel(ticketDetails.status)}
                                            </span>
                                        </div>
                                    </div>

                                    {statusSuccess && <div className="alert alert-success">{statusSuccess}</div>}
                                    {detailError && <div className="alert alert-danger">{detailError}</div>}

                                    <div className="mb-3">
                                        {ticketDetails.status === "resolved" ? (
                                            <button
                                                className="btn btn-outline-secondary btn-sm"
                                                onClick={() => handleStatusChange("waiting_support")}
                                                disabled={statusUpdating}
                                            >
                                                {t.reopenTicket}
                                            </button>
                                        ) : (
                                            <button
                                                className="btn btn-outline-success btn-sm"
                                                onClick={() => handleStatusChange("resolved")}
                                                disabled={statusUpdating}
                                            >
                                                {t.markResolved}
                                            </button>
                                        )}
                                    </div>

                                    <div className="flex-grow-1 border rounded p-3 mb-3 overflow-auto" style={{ maxHeight: "360px" }} ref={messagesContainerRef}>
                                        <h6 className="fw-semibold mb-3">{t.messagesTitle}</h6>
                                        {Array.isArray(ticketDetails.messages) && ticketDetails.messages.length > 0 ? (
                                            ticketDetails.messages.map((message, idx) => (
                                                <div key={message.id || message.created_at} className={`mb-3 pb-3 ${idx !== ticketDetails.messages.length - 1 ? "border-bottom" : ""}`}>
                                                    <div className="d-flex justify-content-between">
                                                        <strong className="text-capitalize">{message.authorName || message.sender || "user"}</strong>
                                                        <small className="text-muted">{formatTimestamp(message.created_at)}</small>
                                                    </div>
                                                    <p className="mb-1" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                                                        {message.body}
                                                    </p>
                                                    {Array.isArray(message.attachments) && message.attachments.length > 0 && (
                                                        <div className="text-muted small">
                                                            {message.attachments.map((file, index) => (
                                                                <div key={`${message.id}-att-${index}`}>
                                                                    <a href={file.url} target="_blank" rel="noreferrer">{file.name || file.url}</a>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            ))
                                        ) : (
                                            <div className="text-muted">{t.noTicketSelected}</div>
                                        )}
                                    </div>

                                    <form onSubmit={handleReply}>
                                        {replyError && <div className="alert alert-danger">{replyError}</div>}
                                        {replySuccess && <div className="alert alert-success">{replySuccess}</div>}
                                        <div className="mb-3">
                                            <label className="form-label">{t.replyPlaceholder}</label>
                                            <textarea
                                                className="form-control"
                                                rows="4"
                                                value={replyText}
                                                onChange={(event) => setReplyText(event.target.value)}
                                                disabled={replySending}
                                            />
                                        </div>
                                        <button type="submit" className="btn btn-primary" disabled={replySending}>
                                            {replySending ? `${t.sendReply}...` : t.sendReply}
                                        </button>
                                    </form>
                                </>
                            ) : (
                                <div className="text-muted text-center my-auto">{t.noTicketSelected}</div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            {showCreateModal && (
                <Dialog
                    title={formSuccess || formError || t.newTicketTitle}
                    closeDialog={closeCreateModal}
                    hideActions
                    footerContent={
                        !formSuccess && (
                            <div className="d-flex justify-content-end gap-2">
                                <button type="button" className="btn btn-outline-secondary" onClick={closeCreateModal} disabled={submitting}>
                                    Cancel
                                </button>
                                <button type="submit" form="ticket-create-form" className="btn btn-primary" disabled={submitting}>
                                    {submitting ? `${t.createTicket}...` : t.createTicket}
                                </button>
                            </div>
                        )
                    }
                >
                    <div ref={formRef}>
                        <p className="text-muted mb-4">{t.newTicketSubtitle}</p>
                        {formError && <div className="alert alert-danger">{formError}</div>}
                        {formSuccess && <div className="alert alert-success">{formSuccess}</div>}
                        {!formSuccess && (
                            <form onSubmit={handleCreateTicket} id="ticket-create-form">
                                <div className="mb-3">
                                    <label className="form-label">{t.priority}</label>
                                    <select
                                        className="form-select"
                                        value={priority}
                                        onChange={(event) => setPriority(event.target.value)}
                                        disabled={submitting}
                                    >
                                        <option value="high">High</option>
                                        <option value="medium">Medium</option>
                                        <option value="low">Low</option>
                                    </select>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">{t.subject}</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={subject}
                                        placeholder="e.g. SSL certificate expired"
                                        onChange={(event) => setSubject(event.target.value)}
                                        disabled={submitting}
                                    />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">{t.description}</label>
                                    <textarea
                                        className="form-control"
                                        rows="5"
                                        value={description}
                                        placeholder="Add as many details as possible"
                                        onChange={(event) => setDescription(event.target.value)}
                                        disabled={submitting}
                                    />
                                </div>
                            </form>
                        )}
                    </div>
                </Dialog>
            )}
        </div>
    );
};

export default Tickets;
